import type { PaginationVariant, SwiperControlsProps, VisibilityMode } from './SwiperControls.types';

const PREVIOUS_SLIDE_ARIA_LABEL = '{{js.shared-component.swiper-controls.previous-slide.aria-label}}';
const NEXT_SLIDE_ARIA_LABEL = '{{js.shared-component.swiper-controls.next-slide.aria-label}}';
const PAGINATION_ARIA_LABEL = '{{js.shared-component.swiper-controls.pagination.aria-label}}';

export const swiperNavigationOptions = {
    prevEl: '.swiper-controls__button--prev',
    nextEl: '.swiper-controls__button--next',
    disabledClass: 'swiper-controls__button--disabled',
    lockClass: 'swiper-controls__button--lock',
};

export const getSwiperPaginationOptions = (variant?: PaginationVariant) => ({
    el: '.swiper-controls__pagination',
    bulletClass: variant ? `swiper-controls__bullet swiper-controls__bullet--${variant}` : 'swiper-controls__bullet',
    bulletActiveClass: 'swiper-controls__bullet--active',
    clickable: true,
    bulletElement: 'button',
});

export const swiperPaginationOptions = getSwiperPaginationOptions();

const getVisibilityMode = (showOnMobile?: boolean, showOnDesktop?: boolean): VisibilityMode => {
    if (showOnMobile && showOnDesktop) return 'both';
    if (showOnMobile) return 'mobile';
    if (showOnDesktop) return 'desktop';
    return 'none';
};

const getNavigationClasses = (
    visibilityMode: VisibilityMode,
    navigationPosition: 'below' | 'sides',
    navigationSidesPosition: 'inside' | 'outside',
): string => {
    const classes = [
        'swiper-controls__navigation',
        `swiper-controls__navigation--${visibilityMode}`,
    ];

    if (navigationPosition === 'sides') {
        classes.push(
            'swiper-controls__navigation--sides',
            navigationSidesPosition === 'outside'
                ? 'swiper-controls__navigation--sides-outside'
                : 'swiper-controls__navigation--sides-inside',
        );
    }

    return classes.join(' ');
};

const getPaginationClasses = (
    visibilityMode: VisibilityMode,
    paginationPosition: 'below' | 'above' | 'inside',
    paginationVariant?: PaginationVariant,
): string => {
    const classes = [
        'swiper-controls__pagination',
        `swiper-controls__pagination--${visibilityMode}`,
    ];

    if (paginationPosition === 'above') {
        classes.push('swiper-controls__pagination--above');
    } else if (paginationPosition === 'inside') {
        classes.push('swiper-controls__pagination--inside');
    }

    if (paginationVariant) {
        classes.push(`swiper-controls__pagination--${paginationVariant}`);
    }

    return classes.join(' ');
};

const SwiperControls = ({
    uniqueId,
    className = '',
    showNavigationMobile,
    showNavigationDesktop,
    showPaginationMobile,
    showPaginationDesktop,
    navigationPosition = 'below',
    navigationSidesPosition = 'inside',
    paginationPosition = 'below',
    paginationVariant,
    isSkeleton = false,
    skeletonPaginationBulletsCount = 3,
}: SwiperControlsProps) => {
    const navigationVisibilityMode = getVisibilityMode(showNavigationMobile, showNavigationDesktop);
    const paginationVisibilityMode = getVisibilityMode(showPaginationMobile, showPaginationDesktop);
    const navigationClasses = getNavigationClasses(navigationVisibilityMode, navigationPosition, navigationSidesPosition);
    const paginationClasses = getPaginationClasses(paginationVisibilityMode, paginationPosition, paginationVariant);

    const prevButtonClass = `swiper-controls__button swiper-controls__button--prev swiper-controls__button--prev-${uniqueId}`;
    const nextButtonClass = `swiper-controls__button swiper-controls__button--next swiper-controls__button--next-${uniqueId}`;
    const paginationClass = `${paginationClasses} swiper-controls__pagination-${uniqueId}`;

    const showNavigation = navigationVisibilityMode !== 'none';
    const showPagination = paginationVisibilityMode !== 'none';
    const shouldRenderSkeletonPagination = isSkeleton && showPagination;
    const skeletonBulletsCount = Math.max(1, Math.floor(skeletonPaginationBulletsCount));

    if (!showNavigation && !showPagination) return null;

    return (
        <div className={['swiper-controls', className].filter(Boolean).join(' ')}>
            {showNavigation &&
                <div className={navigationClasses}>
                    <button
                        type="button"
                        className={prevButtonClass}
                        aria-label={PREVIOUS_SLIDE_ARIA_LABEL}
                    ></button>
                    <button
                        type="button"
                        className={nextButtonClass}
                        aria-label={NEXT_SLIDE_ARIA_LABEL}
                    ></button>
                </div>
            }
            {showPagination &&
                <nav className={paginationClass} aria-label={PAGINATION_ARIA_LABEL}>
                    {shouldRenderSkeletonPagination &&
                        Array.from({ length: skeletonBulletsCount }).map((_, index) => (
                            <button
                                key={`skeleton-bullet-${index}`}
                                type="button"
                                className={[
                                    paginationVariant
                                        ? `swiper-controls__bullet swiper-controls__bullet--${paginationVariant}`
                                        : 'swiper-controls__bullet',
                                    index === 0 && 'swiper-controls__bullet--active',
                                ].filter(Boolean).join(' ')}
                                tabIndex={-1}
                                aria-hidden="true"
                                disabled
                            />
                        ))
                    }
                </nav>
            }
        </div>
    );
};

export default SwiperControls;